import {
  IsBoolean,
  IsDate,
  IsEnum,
  IsInt,
  IsNumber,
  IsString,
  ValidateNested,
} from "class-validator";
import ResponseEntity, { InputEntity, toEnum } from "../..";
import { Transform, Type } from "class-transformer";
import PrivateUser from "../../user";

export enum TransmedEditionSeason {
  "autumn" = "autumn",
  "spring" = "spring",
}

export default class TransmedEdition extends ResponseEntity {
  @IsInt() public readonly id!: number;
  @IsInt() public readonly year!: number;

  @Transform(toEnum(TransmedEditionSeason))
  @IsEnum(TransmedEditionSeason, {
    message: "invalid OutputTransmeEditionSeason",
  })
  public readonly season!: TransmedEditionSeason;

  @IsString() public readonly period_text!: string;
  @IsBoolean() public readonly process_started!: boolean;

  @Type(() => Date)
  @IsDate()
  public readonly registration_start!: Date;

  @Type(() => Date)
  @IsDate()
  public readonly registration_end!: Date;

  @IsNumber() public readonly max_points!: number;

  @Type(() => Date)
  @IsDate()
  public readonly points_deadline_first!: Date;

  @Type(() => Date)
  @IsDate()
  public readonly contesting_deadline!: Date;

  @Type(() => Date)
  @IsDate()
  public readonly points_deadline_second!: Date;

  @Type(() => Date)
  @IsDate()
  public readonly distribution_start!: Date;

  @Type(() => Date)
  @IsDate()
  public readonly exchange_start!: Date;

  @Type(() => Date)
  @IsDate()
  public readonly exchange_end!: Date;

  @Type(() => Date)
  @IsDate()
  public readonly participation_proof_deadline!: Date;

  @IsString() public readonly regulations!: string;

  @Type(() => PrivateUser)
  @ValidateNested()
  public readonly coordinator!: PrivateUser;

  @IsString() public readonly participation_proof_template!: string;
}

export class TransmedEditionInput extends InputEntity {
  public constructor(
    public readonly year: number,
    public readonly season: TransmedEditionSeason,
    public readonly period_text: string,
    public readonly registration_start: Date,
    public readonly registration_end: Date,
    public readonly max_points: number,
    public readonly points_deadline_first: Date,
    public readonly contesting_deadline: Date,
    public readonly points_deadline_second: Date,
    public readonly distribution_start: Date,
    public readonly exchange_start: Date,
    public readonly exchange_end: Date,
    public readonly participation_proof_deadline: Date,
    public readonly regulations: File | null,
    public readonly participation_proof_template: File | null
  ) {
    super();
    if (new Date() > registration_start)
      throw new RangeError(
        "Registration start date should be after the current date."
      );
    if (registration_start > registration_end)
      throw new RangeError(
        "Registration start date should be sooner than registration end date."
      );
    if (registration_end > points_deadline_first)
      throw new RangeError(
        "Registration end date should be sooner than deadline for first points."
      );
    if (points_deadline_first > contesting_deadline)
      throw new RangeError(
        "Deadline for first points should be sooner than deadline for contestations."
      );
    if (contesting_deadline > points_deadline_second)
      throw new RangeError(
        "Deadline for contestations should be sooner than deadline for second points."
      );
    if (points_deadline_second > distribution_start)
      throw new RangeError(
        "Deadline for second points should be sooner than the start of distribution process."
      );
    if (distribution_start > exchange_start)
      throw new RangeError(
        "The start of distribution process should be sooner than start of the exchange."
      );
    if (exchange_start > exchange_end)
      throw new RangeError(
        "The start of the exchange should be sooner than end of the exchange."
      );
    if (exchange_end > participation_proof_deadline)
      throw new RangeError(
        "The end of the exchange should be sooner than the deadline for participation proof upload."
      );
  }
}
